import { FilterInterface } from '@app/src/pages/ResourceCollection/Filters/Filters'
import { InquiryStatus, RequestStatus } from '../types/resourceExplorer'

export enum TypeForFilter {
  Inquiry = 'Inquiry',
  Response = 'Response',
  ResponseScraping = 'ResponseScraping',
}

const transformStatusFromResponseToInquiry = (value: string): string => {
  switch (value) {
    case RequestStatus.ResponseSubmitted:
      return InquiryStatus.Submitted

    case RequestStatus.ResponseApproved:
      return InquiryStatus.Approved

    case RequestStatus.ResponseCorrectionNeeded:
      return InquiryStatus.CorrectionNeeded

    default:
      return value
  }
}

const transformStatusFromInquiryToResponse = (value: string): string => {
  switch (value) {
    case InquiryStatus.Submitted:
      return RequestStatus.ResponseSubmitted

    case InquiryStatus.Approved:
      return RequestStatus.ResponseApproved

    case InquiryStatus.CorrectionNeeded:
      return RequestStatus.ResponseCorrectionNeeded

    default:
      return value
  }
}

const mapFilterValue = (
  sourceViewTypeName: TypeForFilter,
  destinationViewTypeName: TypeForFilter,
  filterName: string,
  filterValue: string | string[],
) => {
  if (
    sourceViewTypeName === TypeForFilter.Inquiry &&
    destinationViewTypeName === TypeForFilter.Response &&
    filterName.toLowerCase() === 'status'
  ) {
    return Array.isArray(filterValue)
      ? filterValue
          .filter(value => value !== InquiryStatus.Requested)
          .map(value => transformStatusFromInquiryToResponse(value))
      : transformStatusFromInquiryToResponse(filterValue)
  }

  if (
    sourceViewTypeName === TypeForFilter.Response &&
    destinationViewTypeName === TypeForFilter.Inquiry &&
    filterName.toLowerCase() === 'request.requeststatus'
  ) {
    return Array.isArray(filterValue)
      ? filterValue
          .filter(value => value !== RequestStatus.ResponseScraped)
          .map(value => transformStatusFromResponseToInquiry(value))
      : transformStatusFromResponseToInquiry(filterValue)
  }

  return filterValue
}

const pathMappings = [
  {
    [TypeForFilter.ResponseScraping]: 'periodName',
    [TypeForFilter.Inquiry]: 'periodName',
    [TypeForFilter.Response]: 'request.periodName',
  },
  {
    [TypeForFilter.ResponseScraping]: 'startedAt',
    [TypeForFilter.Inquiry]: 'createdAt',
    [TypeForFilter.Response]: 'submittedAt',
  },
  {
    [TypeForFilter.ResponseScraping]: undefined,
    [TypeForFilter.Inquiry]: 'status',
    [TypeForFilter.Response]: 'request.requestStatus',
  },
  {
    [TypeForFilter.ResponseScraping]: 'provider',
    [TypeForFilter.Inquiry]: 'provider',
    [TypeForFilter.Response]: 'request.subscriptions.target',
  },
  {
    [TypeForFilter.ResponseScraping]: 'questionnaireTemplate',
    [TypeForFilter.Inquiry]: 'template',
    [TypeForFilter.Response]: 'request.template',
  },
  {
    [TypeForFilter.ResponseScraping]: 'response.verifications.internalVerificationStatus',
    [TypeForFilter.Inquiry]: 'request.responseinternalstatus',
    [TypeForFilter.Response]: 'verifications.internalVerificationStatus',
  },
  {
    [TypeForFilter.ResponseScraping]: 'response.request',
    [TypeForFilter.Inquiry]: 'request',
    [TypeForFilter.Response]: 'request',
  },
  {
    [TypeForFilter.ResponseScraping]: 'response',
    [TypeForFilter.Inquiry]: 'request.response',
    [TypeForFilter.Response]: '',
  },
]

const mapFilterName = (
  sourceViewTypeName: TypeForFilter,
  destinationViewTypeName: TypeForFilter,
  filterName: string,
) => {
  const pathMapping = pathMappings.find(
    p => p[sourceViewTypeName] && filterName.toLowerCase().startsWith(p[sourceViewTypeName].toLowerCase()),
  )
  if (!pathMapping) return filterName
  const newPath = pathMapping[destinationViewTypeName]
  if (!newPath) return undefined
  const oldPathLength = pathMapping[sourceViewTypeName]?.length ?? 0
  return newPath + filterName.substring(oldPathLength)
}

export const mapFiltersBetweenTypes = (
  sourceViewTypeName: TypeForFilter,
  destinationViewTypeName: TypeForFilter,
  filters: FilterInterface[],
) => {
  if (sourceViewTypeName === destinationViewTypeName) return filters
  return filters
    .map(f => ({
      ...f,
      name: mapFilterName(sourceViewTypeName, destinationViewTypeName, f.name),
      value: mapFilterValue(sourceViewTypeName, destinationViewTypeName, f.name, f.value),
    }))
    .filter(f => f.name)
}
